import { Box, Button } from '@mui/material';
import type { Editor } from '@tiptap/react';
import React, { useEffect, useRef } from 'react';
import InlineRichText from '../admin/newContentManagement/InlineRichText';
import { sanitizeFaqEditorHtml } from './sanitizeFaqEditorHtml';

export interface FAQItemEditorProps {
  content: string;
  onSave: (content: string) => void;
  className?: string;
  focusElement?: boolean;
  onFocus?: () => void;
  setExternalInserter?: (inserter: (html: string) => void) => void;
  onRevertToOriginal?: () => void;
}

export default function FAQItemEditor({
  content,
  onSave,
  className,
  focusElement,
  onFocus,
  setExternalInserter,
  onRevertToOriginal,
}: FAQItemEditorProps) {
  const editorRef = useRef<Editor | null>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const externalInserterRef = useRef(setExternalInserter);
  externalInserterRef.current = setExternalInserter;
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  useEffect(
    () => () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    },
    [],
  );

  const handleLiveChange = (html: string) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      onSaveRef.current(sanitizeFaqEditorHtml(html));
    }, 2000);
  };

  const handleSave = (html: string) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    onSaveRef.current(sanitizeFaqEditorHtml(html));
  };

  const handleEditorReady = (editor: Editor) => {
    externalInserterRef.current?.((text) => {
      editor.chain().focus().insertContent(text).run();
    });
  };

  return (
    <Box>
      <InlineRichText
        html={content}
        onChange={handleSave}
        onLiveChange={handleLiveChange}
        onFocus={onFocus}
        onEditorReady={handleEditorReady}
        editorRef={editorRef}
        focusOnMount={focusElement}
        className={className}
        preset="faq"
        ariaLabel="FAQ answer"
        placeholder="Enter answer"
      />
      {onRevertToOriginal && (
        <Button
          size="small"
          onClick={() => {
            if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
            onRevertToOriginal();
          }}
        >
          Revert to original
        </Button>
      )}
    </Box>
  );
}
